import { usePostAuthSignupMutation } from '@/api/serverApi'
import { BrandBackground, BrandIcon, Button, EmailSentIcon, InputLabel, Link, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const signUpFormSchema = v.object({
  company: v.string([v.minLength(1, 'Company name is required')]),
  name: v.string([v.minLength(1, 'Name is required')]),
  email: v.string([v.email('Invalid email address')]),
})

type SignUpFormInputs = v.Input<typeof signUpFormSchema>

const textSpec = {
  title: 'Welcome to Clariti!',
  prompt: {
    text: 'Already have an account?',
    link: 'Sign In',
  },
  subtitle: 'Clariti speeds up the design process of structural components so buildings can be designed better from the start.',
  companyField: {
    label: 'COMPANY NAME',
    placeholder: 'Company',
  },
  nameField: {
    label: 'YOUR NAME',
    placeholder: 'Name',
  },
  emailField: {
    label: 'EMAIL ADDRESS',
    placeholder: 'Email',
  },
  submitButtonLabel: 'Request Access',
  complete: {
    title: 'Check Your Inbox',
    content: 'We\'ve sent an email to you with a link to complete your registration.',
  },
}

function SignUpForm () {
  const errorHandler = useErrorHandler()
  const [isComplete, setIsComplete] = useState(false)

  const { register, handleSubmit, formState: { errors }, setError } = useForm<SignUpFormInputs>({
    defaultValues: {
      company: '',
      name: '',
      email: '',
    },
    resolver: valibotResolver(signUpFormSchema),
  })

  const [signUp, { isLoading: isSubmitting }] = usePostAuthSignupMutation()

  const onSubmit: SubmitHandler<SignUpFormInputs> = (data) => {
    signUp({ body: data })
      .unwrap()
      .then((_) => {
        setIsComplete(true)
      })
      .catch((error) => {
        errorHandler(error, setError)
      })
  }

  if (isComplete) {
    return (
      <div className="flex justify-center w-full mt-[110px]">
        <section className="flex flex-col items-center w-full max-w-[380px]">
          <BrandBackground className="w-[50px] h-[50px]">
            <EmailSentIcon className="h-[28px] w-[28px] text-grey-800" />
          </BrandBackground>

          <h1 className="mb-[15px] mt-[30px] text-4xl leading-[43.2px] font-[450]">{textSpec.complete.title}</h1>
          <p className="mb-[50px] text-base leading-[22.4px] text-center">{textSpec.complete.content}</p>
        </section>
      </div>
    )
  }

  return (
    <form
      className="flex flex-col items-center w-full max-w-[380px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <BrandBackground className="w-[50px] h-[50px]">
        <BrandIcon className="h-[32px] w-[32px] text-grey-800" />
      </BrandBackground>

      <h1 className="mb-[15px] mt-[30px] text-4xl leading-[43.2px] font-[450]">{textSpec.title}</h1>

      <p className="mt-[15px] text-base leading-[22.4px] text-center">
        {textSpec.prompt.text} <Link to="/">{textSpec.prompt.link}</Link>
      </p>

      <p className="mt-[28px] text-base leading-[22.4px] text-center">{textSpec.subtitle}</p>

      <div className="mt-[50px] w-full">
        <InputLabel name="company">{textSpec.companyField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.companyField.placeholder}
          error={errors?.company?.message}
          {...register('company')}
        />
      </div>

      <div className="mt-[30px] w-full">
        <InputLabel name="name">{textSpec.nameField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.nameField.placeholder}
          error={errors?.name?.message}
          {...register('name')}
        />
      </div>

      <div className="mt-[30px] w-full">
        <InputLabel name="email">{textSpec.emailField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.emailField.placeholder}
          error={errors?.email?.message}
          {...register('email')}
        />
      </div>

      <div className="mt-[40px] w-full">
        <Button
          variant="dark"
          isLoading={isSubmitting}
          type="submit"
          isBlock
        >
          {textSpec.submitButtonLabel}
        </Button>
      </div>
    </form>
  )
}

export default SignUpForm
